// Auto-image picker: english word → OpenMoji codepoint. Covers ~250 common
// ESL vocabulary so the teacher can type the word and have the image fill
// in automatically. Falls through to the seed map for the few words already
// covered there.
//
// Codepoints are OpenMoji hex sequences (uppercase, hyphen-separated for
// joined emoji). `openmojiUrl()` builds the final image URL.

import { SEED_IMAGE_CODES, openmojiUrl } from "@shared/seed-images";

const EXTRA: Record<string, string> = {
  // ── Family / people
  baby: "1F476",
  girl: "1F467",
  boy: "1F466",
  woman: "1F469",
  man: "1F468",
  grandfather: "1F474",
  grandmother: "1F475",
  family: "1F46A",
  friend: "1F91D",
  student: "1F9D1-200D-1F393",
  doctor: "1F468-200D-2695-FE0F",
  police: "1F46E",
  chef: "1F468-200D-1F373",

  // ── Body
  eye: "1F441",
  ear: "1F442",
  nose: "1F443",
  mouth: "1F444",
  hand: "270B",
  foot: "1F9B6",
  leg: "1F9B5",
  arm: "1F4AA",
  hair: "1F9B0",
  head: "1F9E0",
  tooth: "1F9B7",
  bone: "1F9B4",

  // ── Animals
  dog: "1F436",
  cat: "1F431",
  rabbit: "1F407",
  horse: "1F40E",
  cow: "1F404",
  pig: "1F416",
  sheep: "1F411",
  goat: "1F410",
  chicken: "1F413",
  duck: "1F986",
  bird: "1F426",
  fish: "1F41F",
  shark: "1F988",
  turtle: "1F422",
  snake: "1F40D",
  lion: "1F981",
  tiger: "1F405",
  bear: "1F43B",
  panda: "1F43C",
  monkey: "1F412",
  elephant: "1F418",
  giraffe: "1F992",
  zebra: "1F993",
  mouse: "1F42D",
  bee: "1F41D",
  butterfly: "1F98B",
  spider: "1F577",
  frog: "1F438",
  dinosaur: "1F995",
  whale: "1F40B",
  dolphin: "1F42C",
  octopus: "1F419",

  // ── Food
  apple: "1F34E",
  banana: "1F34C",
  orange: "1F34A",
  grape: "1F347",
  watermelon: "1F349",
  strawberry: "1F353",
  pineapple: "1F34D",
  lemon: "1F34B",
  peach: "1F351",
  mango: "1F96D",
  bread: "1F35E",
  cake: "1F370",
  cookie: "1F36A",
  donut: "1F369",
  pizza: "1F355",
  burger: "1F354",
  hamburger: "1F354",
  hotdog: "1F32D",
  taco: "1F32E",
  rice: "1F35A",
  noodles: "1F35C",
  egg: "1F95A",
  cheese: "1F9C0",
  milk: "1F95B",
  juice: "1F9C3",
  water: "1F4A7",
  tea: "1F375",
  coffee: "2615",
  candy: "1F36C",
  ice: "1F9CA",
  icecream: "1F368",
  meat: "1F356",
  fishfood: "1F365",
  carrot: "1F955",
  corn: "1F33D",
  tomato: "1F345",
  potato: "1F954",

  // ── Nature / weather
  sun: "2600",
  moon: "1F319",
  star: "2B50",
  cloud: "2601",
  rain: "1F327",
  snow: "2744",
  wind: "1F32C",
  rainbow: "1F308",
  fire: "1F525",
  tree: "1F333",
  flower: "1F33C",
  leaf: "1F343",
  grass: "1F33F",
  mountain: "26F0",
  beach: "1F3D6",
  forest: "1F332",
  desert: "1F3DC",
  ocean: "1F30A",
  river: "1F30A",
  earth: "1F30D",
  rock: "1FAA8",

  // ── Classroom
  book: "1F4D8",
  pen: "1F58A",
  pencil: "270F",
  bag: "1F392",
  ruler: "1F4CF",
  scissors: "2702",
  paper: "1F4C4",
  notebook: "1F4D3",
  desk: "1FA91",
  chair: "1FA91",
  board: "1F4CB",
  computer: "1F4BB",
  clock: "1F570",
  glue: "1F9F4",
  crayon: "1F58D",
  eraser: "1F9F9",
  backpack: "1F392",
  homework: "1F4DD",
  school: "1F3EB",
  classroom: "1F4DA",

  // ── House
  house: "1F3E0",
  bed: "1F6CF",
  door: "1F6AA",
  window: "1FA9F",
  key: "1F511",
  lamp: "1F4A1",
  toilet: "1F6BD",
  bath: "1F6C0",
  shower: "1F6BF",
  kitchen: "1F374",
  fridge: "1F4E5",
  tv: "1F4FA",
  phone: "1F4F1",
  radio: "1F4FB",
  picture: "1F5BC",

  // ── Clothes
  shirt: "1F455",
  tshirt: "1F455",
  pants: "1F456",
  shoe: "1F45F",
  hat: "1F3A9",
  cap: "1F9E2",
  glasses: "1F453",
  watch: "231A",
  socks: "1F9E6",
  gloves: "1F9E4",
  scarf: "1F9E3",
  jacket: "1F9E5",
  dress: "1F457",
  ring: "1F48D",

  // ── Toys & play
  ball: "26BD",
  soccer: "26BD",
  football: "1F3C8",
  basketball: "1F3C0",
  baseball: "26BE",
  tennis: "1F3BE",
  bicycle: "1F6B2",
  car: "1F697",
  bus: "1F68C",
  train: "1F686",
  plane: "2708",
  boat: "1F6A4",
  ship: "1F6A2",
  rocket: "1F680",
  truck: "1F69A",
  taxi: "1F695",
  helicopter: "1F681",
  motorcycle: "1F3CD",
  scooter: "1F6F4",
  game: "1F3AE",
  guitar: "1F3B8",
  piano: "1F3B9",
  drum: "1F941",
  music: "1F3B5",
  art: "1F3A8",
  paint: "1F58C",
  draw: "270F",
  doll: "1F9F8",

  // ── Feelings
  happy: "1F642",
  sad: "1F641",
  angry: "1F620",
  tired: "1F634",
  scared: "1F628",
  surprised: "1F62E",
  love: "2764",
  cry: "1F622",
  laugh: "1F606",
  smile: "1F603",
  sick: "1F912",
  cold: "1F976",
  hot: "1F975",
  hungry: "1F924",
  thirsty: "1F62A",
  excited: "1F929",
  bored: "1F644",

  // ── Actions
  run: "1F3C3",
  walk: "1F6B6",
  jump: "1F998",
  sleep: "1F6CF",
  drink: "1F964",
  eat: "1F37D",
  read: "1F4D6",
  write: "270D",
  look: "1F440",
  listen: "1F442",
  talk: "1F4AC",
  sing: "1F3A4",
  dance: "1F483",
  play: "1F3AE",
  swim: "1F3CA",
  fly: "1F985",
  climb: "26F0",
  kick: "26BD",
  throw: "1F44B",
  catch: "1F94E",
  cook: "1F373",
  wash: "1F9FC",
  clean: "1F9F9",
  push: "1F44D",
  pull: "1FAF2",
  open: "1F4E2",
  close: "1F4EA",
  give: "1F381",
  take: "270B",

  // ── Numbers
  one: "0031-FE0F-20E3",
  two: "0032-FE0F-20E3",
  three: "0033-FE0F-20E3",
  four: "0034-FE0F-20E3",
  five: "0035-FE0F-20E3",
  six: "0036-FE0F-20E3",
  seven: "0037-FE0F-20E3",
  eight: "0038-FE0F-20E3",
  nine: "0039-FE0F-20E3",
  ten: "1F51F",

  // ── Colors (using paint palette + colored hearts/squares)
  red: "1F7E5",
  orange_color: "1F7E7",
  yellow: "1F7E8",
  green: "1F7E9",
  blue: "1F7E6",
  purple: "1F7EA",
  pink: "1FA77",
  white: "2B1C",
  black: "2B1B",
  brown: "1F7EB",

  // ── Misc / nouns kids ask about
  money: "1F4B0",
  gift: "1F381",
  party: "1F389",
  birthday: "1F382",
  balloon: "1F388",
  letter: "1F4E7",
  email: "1F4E7",
  newspaper: "1F4F0",
  map: "1F5FA",
  flag: "1F3F3",
  trophy: "1F3C6",
  medal: "1F3C5",
  bell: "1F514",
  light: "1F4A1",
  battery: "1F50B",
  umbrella: "2602",
  scissor: "2702",
  hammer: "1F528",
  brush: "1F58C",
  soap: "1F9FC",
  toothbrush: "1FAA5",
  bandage: "1FA79",
  medicine: "1F48A",
  vegetables: "1F955",
  fruit: "1F34F",
};

/** Cached merged map (seed + extra). Seed wins for overlapping keys. */
let _merged: Record<string, string> | null = null;
function merged(): Record<string, string> {
  if (_merged) return _merged;
  _merged = { ...EXTRA, ...SEED_IMAGE_CODES };
  return _merged;
}

/**
 * Look up an OpenMoji codepoint for an english word. Tries exact match, then
 * lowercased, then a few simple normalizations (strip article, plural -s/-es).
 */
export function findCodeForWord(english: string): string | null {
  const m = merged();
  const norm = english.trim().toLowerCase();
  if (!norm) return null;
  if (m[norm]) return m[norm]!;
  // Strip leading article ("a apple" → "apple")
  const stripped = norm.replace(/^(a|an|the) /, "");
  if (m[stripped]) return m[stripped]!;
  // Plural → singular
  if (norm.endsWith("ies") && m[norm.slice(0, -3) + "y"]) return m[norm.slice(0, -3) + "y"]!;
  if (norm.endsWith("es") && m[norm.slice(0, -2)]) return m[norm.slice(0, -2)]!;
  if (norm.endsWith("s") && m[norm.slice(0, -1)]) return m[norm.slice(0, -1)]!;
  return null;
}

/** Auto-suggest an image URL. Returns null if no match — caller may show a placeholder. */
export function findImageUrlForWord(english: string): string | null {
  const code = findCodeForWord(english);
  return openmojiUrl(code);
}
